import { providerLogger } from '@ragu-components/logger';
import { buildUrl, maybeThrowHttpError, validateStatus } from '@ragu-components/ord-providers';

import type { UrlOptions } from '@ragu-components/ord-providers';
import type { AxiosInstance } from 'axios';
import type {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin';

export const createImportDataProvider = (
  dataProvider: DataProvider,
  realmId: string,
  axiosInstance: AxiosInstance | undefined
): DataProvider => {
  return {
    getList: async (resource: string, params: GetListParams): Promise<GetListResult> =>
      dataProvider.getList(resource, params),
    getOne: async (resource: string, params: GetOneParams<RaRecord>): Promise<GetOneResult> =>
      dataProvider.getOne(resource, params),
    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> =>
      dataProvider.getMany(resource, params),
    getManyReference: async (resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult> =>
      dataProvider.getManyReference(resource, params),
    create: async (resource: string, params: CreateParams): Promise<CreateResult> => {
      if (axiosInstance === undefined) {
        providerLogger.error('axiosInstance is undefined');
        return { data: {} };
      }
      providerLogger.debug('create import', resource, params);
      const { data, meta } = params;
      const url = buildUrl('users', { realmId, ...(meta as UrlOptions) }).replace('domains', 'import');
      const response = await axiosInstance.post(url, data, { validateStatus });
      maybeThrowHttpError(response);
      const location = response.headers.location;

      // extract id from location ( example location: /api/v1/domains/<domain_id>/taks/<task_id>)
      const taskId = location.split('/').pop();
      if (taskId === undefined) {
        providerLogger.error('taskId is undefined');
        throw new Error('upload failed');
      }
      return { data: { ...data, id: taskId } };
    },
    update: async (resource: string, params: UpdateParams<RaRecord>): Promise<UpdateResult> =>
      dataProvider.update(resource, params),
    updateMany: async (resource: string, params: UpdateManyParams): Promise<UpdateManyResult> =>
      dataProvider.updateMany(resource, params),
    delete: async (resource: string, params: DeleteParams<RaRecord>): Promise<DeleteResult> =>
      dataProvider.delete(resource, params),
    deleteMany: async (resource: string, params: DeleteManyParams<RaRecord>): Promise<DeleteManyResult> =>
      dataProvider.deleteMany(resource, params),
  };
};
